import React, { useCallback, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Box } from '@chakra-ui/react';
import { ArtistModel } from 'models';
import { ArtistGrid, ArtistCreationWizard, Modal } from 'components';
import { NavigationService } from 'services';
import { useArtists, useCreateArtist } from 'hooks/queries/useArtists';
import { ArtistDataSuggestionFormData } from 'components/Form/ArtistDataSuggestionForm/ArtistDataSuggestionForm';
import { convertJSONObjectToSnakecase, filterEmptyValues } from 'utils/object';
import { JSONObject } from 'models/Api/types';
import { useSetCreateAction } from 'contexts/CreateActionContext';
import { MdOutlinePalette } from 'react-icons/md';

const ArtistOverviewView: React.FC = () => {
    const navigate = useNavigate();
    const { data: artistList = [], isLoading: artistListLoading } = useArtists();
    const createArtistMutation = useCreateArtist();
    const [modals, setModals] = useState({
        createArtist: false,
    });

    const handleArtistCardClick = (artist: ArtistModel) =>
        navigate(NavigationService.getArtistDetailsPath(artist.id, artist.name));

    const handleCreateModalOpen = useCallback(() => {
        setModals(prev => ({ ...prev, createArtist: true }));
    }, []);

    const handleCreateModalClose = useCallback(() => {
        setModals(prev => ({ ...prev, createArtist: false }));
        createArtistMutation.reset();
    }, [createArtistMutation]);

    const handleCreateArtistSubmit = (data: ArtistDataSuggestionFormData) => {
        const payload: JSONObject = filterEmptyValues({
            name: data.name,
            description: data.description,
            image: data.image,
            birthYear: data.birthYear,
            country: {
                code: data.country?.code,
                name: data.country?.name,  
            },
            contactInfo: {
                artsyLink: data.artsy,
                instagramLink: data.instagram,
                representativeGallery: data.representativeGallery,
                website: data.website,
            },
        });

        const snakePayload = convertJSONObjectToSnakecase(payload);

        createArtistMutation.mutate(snakePayload, {
            onSuccess: (createdArtistId) => {
                handleCreateModalClose();
                navigate(NavigationService.getArtistDetailsPath(createdArtistId));
            },
        });
    };

    const setCreateAction = useSetCreateAction();

    useEffect(() => {
        setCreateAction({
            icon: MdOutlinePalette,
            label: 'Create',
            onClick: handleCreateModalOpen,
            isLoading: createArtistMutation.isPending,
        });

        return () => setCreateAction(null);
    }, [setCreateAction, handleCreateModalOpen, createArtistMutation.isPending]);

    return (
		<Box maxWidth="1200px" width="100%" paddingTop={['80px', '100px', '100px']} justifySelf="center" minHeight="100vh">
            <ArtistGrid
                artists={artistList}
                isLoading={artistListLoading}
                onArtistCardClick={handleArtistCardClick}
            />
            <Modal
                isOpen={modals.createArtist}
                onClose={handleCreateModalClose}
                title="Create Artist"
                content={(
                    <ArtistCreationWizard
                        onSubmit={handleCreateArtistSubmit}
                        isLoading={createArtistMutation.isPending}
                    />
                )}
                showCloseButton={false}
                showSubmitButton={false}
                size="3xl"
            />
        </Box>
    );
};

export default ArtistOverviewView;
